import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';
import type { Company, CreateCompanyRequest, UpdateCompanyRequest, CompanyPage } from '@mailflow/shared';

export const companiesQuery = (params: { search?: string; limit?: number; cursor?: string } = {}) =>
  queryOptions({
    queryKey: ['companies', params],
    queryFn: ({ signal }) => {
      const sp = new URLSearchParams();
      if (params.search) sp.set('search', params.search);
      if (params.limit) sp.set('limit', String(params.limit));
      if (params.cursor) sp.set('cursor', params.cursor);
      return api.get<CompanyPage>(`/api/companies?${sp.toString()}`, signal);
    },
  });

export const companyQuery = (id: string) =>
  queryOptions({
    queryKey: ['companies', id],
    queryFn: ({ signal }) =>
      api.get<
        Company & {
          contacts: Array<{ id: string; email: string; firstName: string | null; lastName: string | null }>;
          deals: Array<{ id: string; title: string; value: string; status: string }>;
        }
      >(`/api/companies/${id}`, signal),
  });

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCompanyRequest) =>
      api.post<{ company: Company }>('/api/companies', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });
}

export function useUpdateCompany(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCompanyRequest) =>
      api.patch<{ company: Company }>(`/api/companies/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });
}

export function useDeleteCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/api/companies/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });
}
